import React from 'react';
import { Button, Flex, Select, Switch, Text } from '@chatsdet/ui';
import { Plus } from '@phosphor-icons/react';
import type { OrgNotificationDestination } from '@chatsdet/types';

export type { OrgNotificationDestination };

/**
 * Reusable presentation-only editor for a list of Slack destinations
 * (channel + delivery mode + enabled + remove), shared by org notification
 * rules and digest configuration. No data fetching, no dialog chrome — the
 * caller owns the draft state and passes it in via `destinations`/`onChange`.
 */
export function SlackDestinationsPicker({
  destinations,
  onChange,
  channelOptions,
  slackConnected,
  renderExtra,
}: {
  destinations: OrgNotificationDestination[];
  onChange: (next: OrgNotificationDestination[]) => void;
  channelOptions: Array<{ id: string; label: string }>;
  slackConnected: boolean;
  /** Optional extra controls rendered alongside Remove on each destination row (e.g. a "Send Test" button). */
  renderExtra?: (destination: OrgNotificationDestination) => React.ReactNode;
}) {
  const addDestination = () => {
    onChange([
      ...destinations,
      {
        id: crypto.randomUUID(),
        slackChannelId: '',
        deliveryMode: 'post',
        enabled: true,
      },
    ]);
  };

  const updateDestination = (id: string, patch: Partial<OrgNotificationDestination>) => {
    onChange(destinations.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const removeDestination = (id: string) => {
    onChange(destinations.filter((item) => item.id !== id));
  };

  return (
    <div className="space-y-3">
      <Flex justify="between" align="center">
        <Text weight="semibold">Destinations</Text>
        <Button variant="outline" size="sm" onPress={addDestination} isDisabled={!slackConnected}>
          <Plus size={16} className="mr-1.5" />
          Add Destination
        </Button>
      </Flex>

      {!slackConnected && (
        <Text size="sm" className="text-muted-foreground">
          Slack is not connected for this organization. Connect Slack in Settings → Slack to add
          destinations.
        </Text>
      )}

      {slackConnected && destinations.length === 0 && (
        <Text size="sm" className="text-muted-foreground">
          No Slack destination configured yet.
        </Text>
      )}

      {destinations.map((destination) => (
        <div key={destination.id} className="rounded-lg border border-border p-4 space-y-3">
          <Select
            label="Slack Channel"
            items={channelOptions}
            selectedKey={destination.slackChannelId}
            onSelectionChange={(key) => {
              const channelId = String(key);
              updateDestination(destination.id, {
                slackChannelId: channelId,
                slackChannelName:
                  channelOptions.find((channel) => channel.id === channelId)?.label.replace(
                    /^#/,
                    ''
                  ) ?? destination.slackChannelName,
              });
            }}
          />

          <Flex justify="between" align="center" gap="md">
            <Flex align="center" gap="sm">
              <Switch
                isSelected={destination.enabled}
                onChange={(enabled) => updateDestination(destination.id, { enabled })}
              />
              <Text size="sm">Destination enabled</Text>
            </Flex>

            <Flex align="center" gap="sm">
              {renderExtra?.(destination)}
              <Button variant="ghost" size="sm" onPress={() => removeDestination(destination.id)}>
                Remove
              </Button>
            </Flex>
          </Flex>
        </div>
      ))}
    </div>
  );
}
